import { useBoolean, useInterval } from "react-use";
import { PostType } from "../../Post/Post.types";
import { FetchParams } from "../../../Shared/types";
import { useEffect, useRef } from "react";
import { groupPostsUpdatesFetcher } from "../../../DataFetchers/GroupPostsFetcher";

const DELAY_INTERVAL_MILLISECONDS = 60 * 1000;

export function usePostUpdatesTracker({
    currentPosts,
    deleteItem,
    fetchParams,
    additionalArguments,
    updaterFunction,
    numberOfItemsToTrack,
    shouldWatch = false,
}: {
    additionalArguments: object;
    currentPosts: PostType[];
    deleteItem: (key: string, value: number | string) => void;
    fetchParams: FetchParams;
    shouldWatch: boolean;
    updaterFunction: (
        key: string,
        value: number | string,
        update: PostType,
        partialUpdate?: Partial<PostType>,
    ) => void;
    numberOfItemsToTrack?: number;
}) {
    const [hasNewItems, setHasNewItems] = useBoolean(false);
    const abortController = useRef<AbortController>(new AbortController());

    useEffect(() => {
        if (currentPosts.length === 0) {
            setHasNewItems(false);
        }
    }, [currentPosts.length]);

    useEffect(() => {
        abortController.current?.abort();
        abortController.current = new AbortController();
    }, [shouldWatch]);

    useInterval(
        () => {
            if (currentPosts.length === 0) {
                return;
            }
            groupPostsUpdatesFetcher(
                {
                    ...fetchParams,
                    sortCriteria: {
                        ...fetchParams.sortCriteria,
                        limit: numberOfItemsToTrack,
                        interval: DELAY_INTERVAL_MILLISECONDS / 1000,
                    },
                },
                additionalArguments,
                abortController,
            ).then(({ posts, deletedPosts }) => {
                posts.forEach(function (post) {
                    const foundItem = currentPosts.find(
                        ({ id }) => id === post.id,
                    );
                    if (!hasNewItems && !foundItem) {
                        setHasNewItems(true);
                    } else if (foundItem) {
                        updaterFunction("id", post.id, post);
                    }
                });
                currentPosts.forEach((currentPost) => {
                    if (deletedPosts.includes(currentPost.id)) {
                        deleteItem("id", currentPost.id);
                    }
                });
            });
        },
        shouldWatch ? DELAY_INTERVAL_MILLISECONDS : null,
    );

    return { hasNewItems };
}
